import HttpService from "@core/services/http/http.service";
import {
  BankAuthor,
  ChangeInfoResponse,
  ChangePasswordResponse,
  CreateAdmin,
  CreateUser,
  GetDetailResponse,
  GetListResponse,
  UpdateUser,
  User,
  Activities,
} from "@app/types";
import {
  RequestContentType,
  RequestParams,
  Signal,
} from "@core/services/http/http.type";

class _UserService {
  public getList(requestParams?: RequestParams, signal?: Signal) {
    return HttpService.get<GetListResponse<User>>("/admin/api/v2/users", {
      queryParams: { ...requestParams },
      signal,
    });
  }

  public getInfo(signal?: Signal) {
    return HttpService.get<GetDetailResponse<User>>("/user/api/v1/users", {
      signal,
    });
  }

  public changeInfo(fullName: string, email: string) {
    return HttpService.post<ChangeInfoResponse>("/user/api/v1/users/info", {
      body: { fullName, email },
    });
  }

  public changePassword(oldPassword: string, newPassword: string) {
    return HttpService.post<ChangePasswordResponse>(
      "/user/api/v1/auth/change-password",
      {
        body: { oldPassword, newPassword },
      }
    );
  }

  public updateThumbnail(thumbnail: File) {
    return HttpService.post("/user/api/v1/users/thumbnail", {
      body: { thumbnail },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public createAdmin(createAdmin: CreateAdmin) {
    return HttpService.post<User>("/admin/api/v2/admin", {
      body: { ...createAdmin },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public createUser(createUser: CreateUser) {
    return HttpService.post<User>("/admin/api/v2/users", {
      body: { ...createUser },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public deleteUser(userId: string) {
    return HttpService.delete(`/admin/api/v2/users/${userId}`);
  }

  public getUserActivities(userId: number, signal?: Signal) {
    return HttpService.get<GetListResponse<Activities>>(
      `/admin/api/v2/activities/${userId}`,
      {
        signal,
      }
    );
  }

  public updateUser(userId: number, updateUser: Partial<UpdateUser>) {
    return HttpService.patch<User>(`/admin/api/v2/users/${userId}`, {
      body: { ...updateUser },
      requestContentType: RequestContentType.MULTIPART,
    });
  }

  public updatePointUser(userId: number, point: number | string, note?: string) {
    return HttpService.patch("/admin/api/v2/users/point", {
      body: { userId, point, note },
    });
  }

  public resetPassword(userId: number, passwordNew?: string) {
    const body: { userId: number; passwordNew?: string } = { userId };

    if (passwordNew) {
      body.passwordNew = passwordNew;
    }

    return HttpService.patch("/admin/api/v2/users/reset-password", {
      body,
    });
  }

  public getAuthorBankInfo(userId: number) {
    return HttpService.get<GetDetailResponse<BankAuthor>>(
      `/admin/api/v2/author-bank/users/${userId}`
    );
  }

  public updateAuthorBankIsActive(id: number, isActive: boolean | string) {
    return HttpService.patch(`/admin/api/v2/author-bank/${id}`, {
      body: {
        isActive,
      },
    });
  }

  public updateUserVIP(userId: number, voucherCode: string) {
    return HttpService.patch("/admin/api/v2/users/vip", {
      body: {
        userId,
        voucherCode,
      },
    });
  }

  public revokeVIP(userId: number) {
    return HttpService.delete(`/admin/api/v2/users/${userId}/vip-recovery`);
  }
}

const UserService = new _UserService();

export default UserService;
